<template>
  <div class="compensation-insight-tool-container">
    <div class="result-container">
      <CompensationInsightDetail
        :title="$t('全球薪酬洞察')"
        v-model:jobTitle="formData.jobTitle"
        v-model:countryCode="formData.countryCode"
      />
      <div class="main">
        <div class="main-left">
          <div class="result-inner">
            <div class="title">{{ resTitle }}</div>
            <a-form class="result-form" :model="formData" layout="inline">
              <a-select v-model="formData.seniorityLevel" @change="handleShow">
                <a-option v-for="level in seniorityLevelList" :key="level.value" :value="level.value">{{
                  level.text
                }}</a-option>
              </a-select>
              <a-select v-model="calcTime">
                <a-option value="year">{{ $t('年薪') }}</a-option>
                <a-option value="month">{{ $t('月薪') }}</a-option>
              </a-select>
            </a-form>
            <div class="columnar-container">
              <div v-if="isFound" class="columnar">
                <div
                  class="columnar-item"
                  :class="{ active: gradeIndex.includes(index) }"
                  :style="{ height: `${columnarHeight * histogram}px` }"
                  v-for="(histogram, index) in histogramRes?.histogram"
                  :key="index"
                >
                  <div class="desc" v-if="gradeIndex.includes(index)">
                    <div class="salary">${{ columnarBottomData(index)?.salary }}</div>
                    <div class="grade">{{ columnarBottomData(index)?.grade }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="not-found">
                <div class="not-found-tip">当前数据不足</div>
                <div class="not-found-tip">无法显示请重新选择职位或国家</div>
              </div>
            </div>
            <div v-if="isFound" class="tip">* {{ tip }}</div>
          </div>
        </div>
        <div class="main-right">
          <EmploymentTools />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { salaryToolCountryList, salaryToolJobList } from '@/common/global';
import { FormInstance } from '@arco-design/web-vue';
import { isMobile } from '@/common/utils';
import { onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

interface IFormData {
  jobTitle: string;
  countryCode: string;
  seniorityLevel: number;
}

interface IRes {
  confidence: string;
  country: string;
  currency: string;
  d1: number;
  d9: number;
  histogram: number[];
  jobTitle: string;
  median: number;
  scale: string;
  seniorityLevel: number;
  status: string;
}

const { t, locale } = useI18n();
const route = useRoute();
const btnLoading = ref(false);
const columnarHeight = ref(252);
const calcTime = ref('year');
const formData = reactive<IFormData>({
  jobTitle: '',
  countryCode: '',
  seniorityLevel: 3,
});

async function init() {
  formData.jobTitle = (route.query.jobTitle as string) || '';
  formData.countryCode = (route.query.countryCode as string) || '';
  formData.seniorityLevel = Number(route.query.seniorityLevel) || 3;
  try {
    const { data } = await useAsyncData(async () => {
      const { data } = await useFetch<{ data: string }>('/api/resource/salary-insight', {
        method: 'POST',
        body: {
          jobTitle: route.query.jobTitle,
          countryCode: route.query.countryCode,
          seniorityLevel: route.query.seniorityLevel || 3,
        },
      });
      return { data: data.value?.data };
    });
    const res = JSON.parse(data.value?.data || '') as IRes;
    histogramRes.value = res;
  } catch (error) {
    console.info('获取数据失败');
  }
}

const seniorityLevelList = computed(() => [
  {
    value: 1,
    text: `${t('职位级别')}-${t('初级')}`,
  },
  {
    value: 2,
    text: `${t('职位级别')}-${t('中级')}`,
  },
  {
    value: 3,
    text: `${t('职位级别')}-${t('高级')}`,
  },
]);

const histogramRes = ref<IRes | null>(null);
if (route.query.jobTitle && route.query.countryCode) {
  await init();
}

const gradeIndex = computed(() => {
  if (!histogramRes.value) {
    return [];
  }
  return [0, histogramRes.value.histogram.length - 1, histogramRes.value.histogram.findIndex(h => h === 1)];
});

const columnarBottomData = (index: number) => {
  const res = {
    salary: '',
    grade: '',
  };
  if (!histogramRes.value) {
    return res;
  }
  const { d1, d9, median } = histogramRes.value;
  let minWage = d1;
  let maxWage = d9;
  let medianWage = median;
  if (calcTime.value !== 'year') {
    minWage = Math.ceil(d1 / 12);
    maxWage = Math.ceil(d9 / 12);
    medianWage = Math.ceil(median / 12);
  }
  switch (index) {
    case 0:
      res.salary = minWage > 1000 ? parseInt(`${minWage / 1000}`) + 'K' : `${minWage}`;
      res.grade = t('最低工资');
      break;
    case histogramRes.value?.histogram.length - 1:
      res.salary = maxWage > 1000 ? parseInt(`${maxWage / 1000}`) + 'K' : `${maxWage}`;
      res.grade = t('最高工资');
      break;
    default:
      res.salary = medianWage > 1000 ? parseInt(`${medianWage / 1000}`) + 'K' : `${medianWage}`;
      res.grade = t('中位数工资');
      break;
  }
  return res;
};

const formRef = ref<FormInstance | null>(null);
const handleShow = async () => {
  const res = await formRef.value?.validate();
  if (res) {
    return false;
  }
  const countryName = salaryToolCountryList.find(c => c.value === formData.countryCode)?.label;
  useLogger().track('compensation-insight-tool', {
    countryName,
    ...formData,
  });
  btnLoading.value = true;
  await navigateTo({
    path: route.path,
    query: {
      ...route.query,
      ...formData,
    },
  });
  window.location.reload();
};

const isFound = computed(() => {
  return histogramRes.value?.status === 'found';
});

const job = computed(() => {
  const item = salaryToolJobList.find(s => s.value === route.query.jobTitle);
  return locale.value === 'zh-CN' ? item?.label : item?.value;
});
const country = computed(() => {
  const item = salaryToolCountryList.find(s => s.value === route.query.countryCode);
  return locale.value === 'zh-CN' ? item?.label : item?.enLabel;
});
const resTitle = computed(() => {
  return `${country.value} ${job.value} ${t('薪酬')}`;
});

useSeoHead({
  title: country.value ?? '',
});

const tip = computed(() => {
  const levelMap: Record<number, string> = {
    1: '初级',
    2: '中级',
    3: '高级',
  };
  return `${country.value}${levelMap[Number(route.query.seniorityLevel) || 3]}【${job.value}】${
    calcTime.value === 'year' ? t('年薪') : t('月薪')
  } ${t('中位数')} ${parseInt(
    (calcTime.value === 'year' ? histogramRes.value?.median : (histogramRes.value?.median || 0) / 12) + ''
  )} USD，${t('工资估算是根据')}${country.value}【${job.value}】${t('雇员匿名提交给DG.hire的资料，仅供参考')}`;
});

onMounted(() => {
  columnarHeight.value = isMobile() ? 200 : 252;
});
</script>

<style lang="less" scoped>
.compensation-insight-tool-container {
  padding-bottom: 66px;
  background-color: #f5f8ff;

  .computer-container {
    width: 680px;
    height: 350px;
    background: #ffffff;
    border-radius: 12px;
    margin: -50px auto 0px;
    overflow: hidden;

    .title {
      font-weight: 500;
      font-size: 24px;
      line-height: 34px;
      text-align: center;
      color: #1d2129;
      margin-top: 32px;
    }

    .form {
      width: 400px;
      margin: 0 auto;
      margin-top: 24px;

      :deep(.arco-row) {
        .arco-input-wrapper,
        .arco-select {
          background-color: #fff;
          border: 1px solid #c9cdd4;
          border-radius: 4px;
          padding: 0px 12px 0px 20px;

          .arco-input,
          .arco-select-view-input,
          .arco-select-view-value {
            font-size: 16px;

            &::placeholder {
              color: #86909c;
              font-size: 16px;
            }
          }

          .arco-select-view-prefix,
          .arco-input-prefix {
            padding-right: 43px;
            position: relative;

            .amount-prefix {
              font-weight: 400;
              font-size: 18px;
              line-height: 22px;
              color: #1d2129;
            }

            &::after {
              content: '';
              position: absolute;
              width: 1px;
              height: 20px;
              background-color: #e5e6eb;
              right: 23px;
            }
          }
        }
      }
    }

    .btn {
      width: 400px;
      height: 48px;
      margin: 12px auto;
      display: block;
      font-weight: 500;
      font-size: 16px;
      border-radius: 4px;
    }

    .tip {
      font-weight: 400;
      font-size: 16px;
      line-height: 22px;
      text-align: center;
      color: #4e5969;
      margin-top: 12px;
    }
  }

  .result-container {
    margin: 0 auto;
    width: fit-content;

    .main {
      display: flex;
      justify-content: center;

      .main-left {
        position: relative;
      }

      .main-right {
        margin-top: 24px;
        margin-left: 24px;
      }
    }

    .result-inner {
      width: 680px;
      height: 628px;
      box-sizing: border-box;
      background: linear-gradient(179.89deg, rgba(222, 255, 229, 0.2) 0.1%, rgba(188, 210, 255, 0) 6.08%), #ffffff;
      border: 1px solid #e8f0ff;
      border-radius: 8px;
      margin: 0 auto;
      margin-top: 24px;
      padding: 40px;

      .result-form {
        margin-top: 16px;

        :deep(.arco-select) {
          min-width: 160px;
          width: min-content;
          margin-right: 16px;
          background-color: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
        }
      }

      .title {
        font-weight: 500;
        font-size: 24px;
        line-height: 34px;
        color: #1d2129;
      }

      .columnar-container {
        width: 100%;
        height: 370px;
        background: #ffffff;
        border-radius: 12px;
        margin-top: 48px;
        padding: 0px 40px 0;
        box-sizing: border-box;

        .columnar {
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          height: 252px;
          gap: 8px;

          .columnar-item {
            background: #d6e1ff;
            border-radius: 4px;
            width: -webkit-fill-available;
            transition: height 0.3s ease;
            position: relative;
            display: flex;
            justify-content: center;

            &.active {
              background: #4b63a5;
            }

            .desc {
              position: absolute;
              bottom: -90px;
              white-space: nowrap;

              .salary {
                font-weight: 500;
                font-size: 24px;
                line-height: 34px;
                text-align: center;
                color: #1d2129;
              }

              .grade {
                font-weight: 400;
                font-size: 20px;
                line-height: 28px;
                text-align: center;
                color: #4e5969;
                margin-top: 6px;
              }
            }
          }
        }

        .not-found {
          padding-top: 190px;
          overflow: hidden;
          overflow: hidden;
          height: 100%;
          background: url('@/assets/icons/salary-tool-not-found.svg') no-repeat center 54px/120px 120px;

          .not-found-tip {
            font-weight: 400;
            font-size: 20px;
            line-height: 32px;
            text-align: center;
            color: #86909c;
          }
        }
      }

      .tip {
        font-weight: 400;
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        color: #86909c;
        padding: 0 60px;
        margin-top: 20px;
      }
    }
  }
}

@media screen and (min-width: 800px) {
  .compensation-insight-tool-container {
    min-width: 1344px;
  }
  .mobile_button {
    display: none;
  }
}

@media screen and (max-width: 800px) {
  .compensation-insight-tool-container {
    width: 100%;
    padding-bottom: 32px;

    .main {
      padding: 0 16px;
      display: block !important;
      .main-right {
        display: none;
      }
      .pc_button {
        display: none;
      }
    }

    .computer-container {
      width: calc(100% - 32px);
      margin: -49px auto 0;
      padding: 0 16px 24px;
      box-sizing: border-box;
      height: auto;

      .title {
        font-size: 20px;
        line-height: 28px;
        margin-top: 24px;
      }

      .tip {
        font-weight: 400;
        font-size: 14px;
        line-height: 20px;
        margin-top: 8px;
      }

      .form {
        margin-top: 24px;
        width: 100%;

        :deep(.arco-form-item-content-wrapper) {
          .arco-select-view-single,
          .arco-input-wrapper {
            height: 48px;

            .arco-select-view-prefix,
            .arco-input-prefix {
              padding-right: 24px;

              &::after {
                right: 11px;
              }

              .amount-prefix {
                font-size: 16px;
              }
            }
          }
        }

        :deep(.arco-form-item) {
          margin-bottom: 16px;
        }
      }

      .btn {
        width: 100%;
        margin-top: 8px;
        margin-bottom: 0;
      }
    }

    .result-container {
      padding-top: 16px;
      width: 100%;

      .result-inner {
        width: 100%;
        padding: 16px;
        height: auto;

        .title {
          font-weight: 500;
          font-size: 20px;
          line-height: 28px;
        }

        .result-form {
          :deep(.arco-select) {
            width: fit-content;
            min-width: unset;
          }
        }

        .columnar-container {
          margin-top: 16px;
          padding: 24px 22px 0;
          height: 300px;

          .columnar {
            height: 200px;

            .columnar-item {
              .desc {
                bottom: -50px;

                .salary {
                  font-weight: 500;
                  font-size: 16px;
                  line-height: 22px;
                }

                .grade {
                  font-weight: 400;
                  font-size: 12px;
                  line-height: 17px;
                  margin-top: 4px;
                }
              }
            }
          }
        }

        .tip {
          padding: 0;
          margin-top: 12px;
        }
      }
    }
  }
}
</style>
